import React, { useState, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import md5 from "md5";
import { toast } from "react-toastify";
import {
  Box,
  Paper,
  Stack,
  Tabs,
  Tab,
  TextField,
  Button,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import mainLogo from "../../../assets/images/dhundhoo_logo.png";

const AUTH_BASE = "https://v3.dhundhoo.com/auth";
const RESEND_OTP_SECONDS = 20;

// ── shared styles ────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100dvh",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "radial-gradient(circle at 15% 20%, rgba(243,149,7,0.12), transparent 45%)," +
      "radial-gradient(circle at 85% 80%, rgba(0,201,167,0.14), transparent 45%)," +
      "#07112a",
    padding: "16px",
    fontFamily: "'Inter', sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: 400,
    height: "fit-content",
    borderRadius: 16,
    padding: "22px 20px",
    background: "#ffffff",
    boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
  },
  logoRow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    marginBottom: 5,
  },
  logoImg: { width: 36, height: 36, objectFit: "contain" },
  title: { fontWeight: 800, color: "#0f1b35", textAlign: "center", fontSize: "1.05rem" },
  subtitle: {
    color: "#64748b",
    textAlign: "center",
    fontSize: "0.75rem",
    lineHeight: 1.4,
  },
};

const initialTimerRef = () => ({ interval: null });

function DeleteUser() {
  // which account type: "parent" | "admin"
  const [accountType, setAccountType] = useState("parent");

  // shared session/flow state
  const [accessCode, setAccessCode] = useState("");
  const [session, setSession] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // parent flow state
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendTimer, setResendTimer] = useState(RESEND_OTP_SECONDS);
  const [canResend, setCanResend] = useState(false);
  const timerBox = useRef(initialTimerRef());

  // admin flow state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [adminSigningIn, setAdminSigningIn] = useState(false);

  useEffect(() => {
    return () => {
      if (timerBox.current.interval) clearInterval(timerBox.current.interval);
    };
  }, []);

  const startResendTimer = () => {
    setCanResend(false);
    setResendTimer(RESEND_OTP_SECONDS);
    if (timerBox.current.interval) clearInterval(timerBox.current.interval);
    timerBox.current.interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerBox.current.interval);
          setCanResend(true);
          return RESEND_OTP_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const resetFlow = () => {
    setAccessCode("");
    setSession("");
    setOtp("");
    setOtpSent(false);
    setMobile("");
    setEmail("");
    setPassword("");
    if (timerBox.current.interval) clearInterval(timerBox.current.interval);
  };

  const switchAccountType = (_e, value) => {
    setAccountType(value);
    resetFlow();
  };

  // ── PARENT: send OTP ───────────────────────────────────────────
  const handleParentSignIn = async () => {
    if (!mobile || mobile.trim().length < 10) {
      toast.error("Please enter a valid mobile number");
      return;
    }
    setSigningIn(true);
    try {
      const response = await fetch(`${AUTH_BASE}/signin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          installToken: "",
          mode: "PHONE",
          phoneNumber: `91${mobile.trim()}`,
        }),
      });
      const data = await response.json();
      if (data && data.accessCode) {
        setAccessCode(data.accessCode);
        setOtpSent(true);
        startResendTimer();
        toast.success("OTP sent successfully");
      } else {
        throw new Error("Account does not exist");
      }
    } catch (error) {
      toast.error(error.message || "Account does not exist");
    } finally {
      setSigningIn(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const response = await fetch(`${AUTH_BASE}/resendOtp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          installToken: "",
          mode: "PHONE",
          phoneNumber: `91${mobile.trim()}`,
        }),
      });
      const text = await response.text();
      if (text === "success" || text.includes("success")) {
        toast.success("OTP sent successfully");
        startResendTimer();
      } else {
        toast.error("Something went wrong, please try again.");
      }
    } catch (error) {
      toast.error("Failed to resend OTP.");
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      toast.error("Please enter the OTP");
      return;
    }
    setVerifyingOtp(true);
    try {
      const response = await fetch(`${AUTH_BASE}/verifyotp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessCode, otp }),
      });
      const text = await response.text();
      let ok = text === "success";
      if (!ok) {
        try {
          ok = JSON.parse(text) === "success";
        } catch (e) {
          ok = false;
        }
      }
      if (!ok) throw new Error("Wrong OTP");

      if (timerBox.current.interval) clearInterval(timerBox.current.interval);
      await authorizeParent();
    } catch (error) {
      toast.error(error.message || "Wrong OTP");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const authorizeParent = async () => {
    try {
      const response = await fetch(`${AUTH_BASE}/authorize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accessCode,
          manufacturer: "Google",
          osVersion: "1",
          platform: "ANDROID",
          timeZone: "Asia/Calcutta",
          version: "1.0.0",
        }),
      });
      const data = await response.json();
      if (data && data.session) {
        setSession(data.session);
        toast.success("Account verified");
        setConfirmOpen(true);
      } else {
        throw new Error("Account not valid");
      }
    } catch (error) {
      toast.error(error.message || "Account not valid");
    }
  };

  // ── ADMIN: sign in with email/password ─────────────────────────
  const handleAdminSignIn = async () => {
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }
    setAdminSigningIn(true);
    try {
      const deviceId = uuidv4();
      const response = await fetch(`${AUTH_BASE}/signin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          emailId: email.trim(),
          encodedPassword: md5(password),
          mode: "EMAIL",
          installToken: deviceId,
        }),
      });
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error("Invalid response from server");
      }
      if (!response.ok || !data.accessCode) {
        throw new Error(data.message || "Sign-in failed");
      }
      setAccessCode(data.accessCode);
      await authorizeAdmin(data.accessCode);
    } catch (error) {
      toast.error(error.message || "Failed to sign in");
    } finally {
      setAdminSigningIn(false);
    }
  };

  const authorizeAdmin = async (code) => {
    try {
      const response = await fetch(`${AUTH_BASE}/authorize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accessCode: code,
          platform: "WEB",
          version: "0.2.0",
        }),
      });
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error("Invalid response from server");
      }
      if (!response.ok || !data.session) {
        throw new Error(data.message || "Authorization failed");
      }
      setSession(data.session);
      toast.success("Account verified");
      setConfirmOpen(true);
    } catch (error) {
      toast.error(error.message || "Failed to authorize");
    }
  };

  // ── shared: delete account ──────────────────────────────────────
  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const codeParam = encodeURIComponent(accessCode);
      const response = await fetch(
        `${AUTH_BASE}/account/parent/remove/${codeParam}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-dhundhoo-session": session,
          },
        }
      );
      const text = await response.text();
      let ok = text === "success";
      if (!ok) {
        try {
          ok = JSON.parse(text) === "success";
        } catch (e) {
          ok = false;
        }
      }
      if (ok) {
        toast.success("Account deleted successfully");
        setConfirmOpen(false);
        resetFlow();
      } else {
        toast.error("Account not deleted");
      }
    } catch (error) {
      toast.error("Failed to fetch data from the API.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={S.page}>
      <Paper elevation={0} sx={S.card}>
        <Box sx={S.logoRow}>
          <img src={mainLogo} alt="Dhundhoo" style={S.logoImg} />
          <Typography variant="h6" sx={S.title}>
            Delete Account
          </Typography>
          <Typography sx={S.subtitle}>
            Verify your identity to permanently delete your Dhundhoo account
          </Typography>
        </Box>

        <Tabs
          value={accountType}
          onChange={switchAccountType}
          variant="fullWidth"
          sx={{
            mb: 2,
            minHeight: 36,
            "& .MuiTab-root": { minHeight: 36, py: 0.5, textTransform: "none", fontWeight: 600, fontSize: "0.8rem" },
          }}
        >
          <Tab label="Parent Account" value="parent" />
          <Tab label="Admin Account" value="admin" />
        </Tabs>

        {accountType === "parent" ? (
          <Box>
            <TextField
              fullWidth
              size="small"
              label="Mobile number"
              placeholder="10-digit mobile number"
              value={mobile}
              disabled={otpSent}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">+91</InputAdornment>
                ),
              }}
              sx={{ mb: 1.5 }}
            />

            {!otpSent ? (
              <Button
                fullWidth
                variant="contained"
                size="medium"
                onClick={handleParentSignIn}
                disabled={signingIn}
                sx={{ textTransform: "none", fontWeight: 700, py: 0.9, mb: 0.5, color: "#fff", backgroundColor: "#f39507", "&:hover": { backgroundColor: "#e07b00" } }}
              >
                {signingIn ? <CircularProgress size={20} color="inherit" /> : "Send OTP"}
              </Button>
            ) : (
              <>
                <TextField
                  fullWidth
                  size="small"
                  label="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  sx={{ mb: 1.5 }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="medium"
                  onClick={handleVerifyOtp}
                  disabled={verifyingOtp}
                  sx={{ textTransform: "none", fontWeight: 700, py: 0.9, mb: 0.5, color: "#fff", backgroundColor: "#f39507", "&:hover": { backgroundColor: "#e07b00" } }}
                >
                  {verifyingOtp ? <CircularProgress size={20} color="inherit" /> : "Verify OTP"}
                </Button>
                <Box sx={{ textAlign: "center" }}>
                  {canResend ? (
                    <Button size="small" onClick={handleResendOtp} sx={{ textTransform: "none" }}>
                      Resend OTP
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Resend OTP in {resendTimer}s
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Box>
        ) : (
          <Box>
            <TextField
              fullWidth
              size="small"
              type="email"
              label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 1.5 }}
            />
            <TextField
              fullWidth
              size="small"
              type={showPassword ? "text" : "password"}
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1.5 }}
            />
            <Button
              fullWidth
              variant="contained"
              size="medium"
              onClick={handleAdminSignIn}
              disabled={adminSigningIn}
              sx={{ textTransform: "none", fontWeight: 700, py: 0.9,color: "#fff", backgroundColor: "#f39507", "&:hover": { backgroundColor: "#e07b00" } }}
            >
              {adminSigningIn ? <CircularProgress size={20} color="inherit" /> : "Sign in"}
            </Button>
          </Box>
        )}
      </Paper>

      <Dialog open={confirmOpen} onClose={() => !deleting && setConfirmOpen(false)}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DeleteForeverIcon color="error" />
          Account Deletion
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this account permanently? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAccount}
            disabled={deleting}
            sx={{ textTransform: "none" }}
          >
            {deleting ? <CircularProgress size={20} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DeleteUser;